import React from 'react';
import { Button, Flex, Typography } from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import { refreshPrerenderUser } from 'actions/prerenderUserActions';
import { AppDispatch } from '../reduxTypes';
import requireAuth from '../components/hocs/requireAuth';
import AdminTemplate from '../layout/AdminTemplate';
// Relative and `~`-aliased forms only: vitest resolves neither `layout/` nor
// `hooks/`, so the src-rooted bare forms left this page untestable.
import FullPageHeightContainer from '../layout/FullPageHeightContainer';
import { useHasTrackingCode, usePrerenderUser, useUserRole } from '../hooks/usePrerenderUser';
import { UserRole } from '~features/PrerenderUser';
import {
  dismissSurvey,
  OnboardingSurvey,
  useAnswersShortSurvey,
  type SurveyCompletion,
} from '~features/OnboardingSurvey';

const META_TAGS = {
  title: 'Onboarding Survey - Prerender.io',
};

function SurveyCompleted({ integrationCompleted = false }) {
  return (
    <Flex vertical gap="large" align="center">
      <Typography.Title level={3}>Thanks for completing the survey! Your feedback is valuable to us.</Typography.Title>

      {integrationCompleted && (
        <Link to="/">
          <Button type="primary">Proceed to the Dashboard</Button>
        </Link>
      )}

      {!integrationCompleted && (
        <Typography.Paragraph>Please ask your team to complete the integration.</Typography.Paragraph>
      )}
    </Flex>
  );
}

function OnboardingSurveyPage() {
  const navigate = useNavigate();
  const user = usePrerenderUser();
  const role = useUserRole();
  const answersShortSurvey = useAnswersShortSurvey();
  const trackingCodeInstalled = useHasTrackingCode();
  // Typed so the thunk's resolved value survives: the completion decision is made on
  // the record `refreshPrerenderUser` resolves with.
  const dispatch = useDispatch<AppDispatch>();

  const surveyCompleted = user.onboardingSurveyCompleted;

  // Reading the user back is the only thing that makes the completion hold, because
  // the flag is recomputed server-side on every read (PRE-3350). When it comes back
  // false the answers are stored but the survey is not done, and navigating would
  // only bounce off `redirectIfNeeded` straight back to here.
  const onCompleteSurvey = async (): Promise<SurveyCompletion> => {
    const { onboardingSurveyCompleted } = await dispatch(refreshPrerenderUser());
    if (!onboardingSurveyCompleted) {
      return { confirmed: false };
    }

    if (role !== UserRole.Guest) {
      navigate('/');
    }

    return { confirmed: true };
  };

  const onAbandonSurvey = () => {
    dismissSurvey();
    navigate('/');
  };

  return (
    <AdminTemplate metaTags={META_TAGS} hasSidebar={false} hasNavigation={false}>
      <FullPageHeightContainer>
        <Flex style={{ paddingTop: 8 }} justify="center">
          {surveyCompleted && <SurveyCompleted integrationCompleted={trackingCodeInstalled} />}
          {!surveyCompleted && (
            <OnboardingSurvey
              isShortSurvey={answersShortSurvey}
              onCompleteSurvey={onCompleteSurvey}
              onAbandonSurvey={onAbandonSurvey}
              getContinueLabel={() => 'Continue to Dashboard'}
            />
          )}
        </Flex>
      </FullPageHeightContainer>
    </AdminTemplate>
  );
}

export default requireAuth(OnboardingSurveyPage);
